import { createConsumer } from '@rails/actioncable'
import { AnimatePresence, motion } from 'framer-motion'
import { AlertTriangle, Check, Clock, Eye, EyeOff, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useModal } from './ModalContext'

// ─── Masks ────────────────────────────────────────────────────────────────────

function maskPhone(v) {
  const d = v.replace(/\D/g, '').slice(0, 11)
  if (!d) return ''
  if (d.length <= 2) return `(${d}`
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

function maskCpfCnpj(v) {
  const d = v.replace(/\D/g, '')
  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
  }
  return d
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
}

function maskDate(v) {
  return v
    .replace(/\D/g, '')
    .slice(0, 8)
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2')
}

function maskCep(v) {
  return v.replace(/\D/g, '').slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2')
}

function maskCardNumber(v) {
  return v.replace(/\D/g, '').slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ')
}

function maskCardExpiry(v) {
  return v.replace(/\D/g, '').slice(0, 4).replace(/(\d{2})(\d)/, '$1/$2')
}

// ─── Pagar.me tokenização (client-side; o PAN nunca toca o nosso servidor) ──────

const PAGARME_TOKENS_URL = 'https://api.pagar.me/core/v5/tokens'

async function tokenizeCard(publicKey, card) {
  const res = await fetch(`${PAGARME_TOKENS_URL}?appId=${encodeURIComponent(publicKey)}`, {
    method: 'POST',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify({
      type: 'card',
      card: {
        number: card.number.replace(/\D/g, ''),
        holder_name: card.holder.trim(),
        exp_month: Number(card.expiry.slice(0, 2)),
        exp_year: Number(card.expiry.slice(3, 5)),
        cvv: card.cvv.replace(/\D/g, ''),
      },
    }),
  })

  if (!res.ok) return null

  const body = await res.json()
  return body.id || null // token_xxx
}

// ─── Validators ───────────────────────────────────────────────────────────────

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())
}

function isValidCpf(d) {
  if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false
  let s = 0
  for (let i = 0; i < 9; i++) s += +d[i] * (10 - i)
  let r = (s * 10) % 11; if (r >= 10) r = 0
  if (r !== +d[9]) return false
  s = 0
  for (let i = 0; i < 10; i++) s += +d[i] * (11 - i)
  r = (s * 10) % 11; if (r >= 10) r = 0
  return r === +d[10]
}

function isValidCnpj(d) {
  if (d.length !== 14 || /^(\d)\1+$/.test(d)) return false
  const calc = (len) => {
    let s = 0, p = len - 7
    for (let i = len; i >= 1; i--) { s += +d[len - i] * p--; if (p < 2) p = 9 }
    const r = s % 11; return r < 2 ? 0 : 11 - r
  }
  return calc(12) === +d[12] && calc(13) === +d[13]
}

function isValidCpfOrCnpj(v) {
  const d = v.replace(/\D/g, '')
  if (d.length === 11) return isValidCpf(d)
  if (d.length === 14) return isValidCnpj(d)
  return false
}

function isValidDate(v) {
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return false
  const [dd, mm, yyyy] = v.split('/').map(Number)
  const date = new Date(yyyy, mm - 1, dd)
  return date.getFullYear() === yyyy && date.getMonth() === mm - 1 && date.getDate() === dd
}

// ─── Crop & center (off-screen canvas) ───────────────────────────────────────

function cropAndCenter(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const offscreen = document.createElement('canvas')
      offscreen.width  = img.naturalWidth
      offscreen.height = img.naturalHeight
      const ctx = offscreen.getContext('2d')
      ctx.drawImage(img, 0, 0)

      const { data, width, height } = ctx.getImageData(0, 0, offscreen.width, offscreen.height)

      let top = height, bottom = 0, left = width, right = 0

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const i = (y * width + x) * 4
          const [r, g, b, a] = [data[i], data[i+1], data[i+2], data[i+3]]
          if (a > 20 && !(r > 240 && g > 240 && b > 240)) {
            if (y < top)    top    = y
            if (y > bottom) bottom = y
            if (x < left)   left   = x
            if (x > right)  right  = x
          }
        }
      }

      if (top > bottom || left > right) { resolve(dataUrl); return }

      const pad   = 24
      const cropX = Math.max(left   - pad, 0)
      const cropY = Math.max(top    - pad, 0)
      const cropW = Math.min(right  - left + pad * 2, width  - cropX)
      const cropH = Math.min(bottom - top  + pad * 2, height - cropY)

      const out = document.createElement('canvas')
      out.width  = cropW
      out.height = cropH
      out.getContext('2d').drawImage(offscreen, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH)

      resolve(out.toDataURL('image/png'))
    }
    img.src = dataUrl
  })
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const LABEL = 'block text-[13px] font-semibold text-text-secondary mb-1.5'
const ERROR = 'text-[11px] text-status-error mt-1'

function inputCls(hasError) {
  return [
    'w-full rounded-input border px-4 py-3 text-sm text-text-primary',
    'bg-surface-page placeholder:text-text-muted',
    'focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-border-input-focus',
    'transition-colors',
    hasError ? 'border-status-error' : 'border-border-input',
  ].join(' ')
}

function methodCls(active) {
  return [
    'rounded-input border px-4 py-3 text-left transition-colors',
    active ? 'border-accent-primary bg-accent-primary/5' : 'border-border-input hover:border-text-muted',
  ].join(' ')
}

// ─── Step tabs ────────────────────────────────────────────────────────────────

const TABS = ['1. Seus dados', '2. Cadastro', '3. Endereço', '4. Assinatura', '5. Pagamento']

function StepTabs({ current }) {
  return (
    <div className="flex gap-6 border-b border-border-input mb-5">
      {TABS.map((label, i) => {
        const active = current === i + 1
        return (
          <span
            key={label}
            className={`pb-3 text-sm font-semibold transition-colors select-none ${
              active
                ? 'text-accent-primary border-b-2 border-accent-primary -mb-px'
                : 'text-text-muted'
            }`}
          >
            {label}
          </span>
        )
      })}
    </div>
  )
}

// ─── Field ────────────────────────────────────────────────────────────────────

function Field({ label, error, children }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      {children}
      {error && <p className={ERROR}>{error}</p>}
    </div>
  )
}

// ─── Outcome screen (sucesso / erro / confirmação) ─────────────────────────────

const OUTCOME_TONES = {
  success: 'bg-status-success/10 text-status-success',
  error:   'bg-status-error/10 text-status-error',
  info:    'bg-accent-primary/10 text-accent-primary',
}

function OutcomeScreen({ tone = 'info', icon: Icon, title, children, actions }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 ${OUTCOME_TONES[tone]}`}>
        <Icon className="w-8 h-8" strokeWidth={2.5} />
      </div>
      <h2 className="text-[20px] font-bold text-text-primary mb-2">{title}</h2>
      <div className="text-[14px] text-text-secondary max-w-xs">{children}</div>
      {actions && <div className="flex flex-col gap-2 w-full max-w-xs mt-6">{actions}</div>}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

const EMPTY = {
  nome: '', sobrenome: '', email: '', telefone: '',
  cpfCnpj: '', dataNascimento: '', senha: '', confirmarSenha: '',
  cep: '', rua: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '',
  cardNumber: '', cardHolder: '', cardExpiry: '', cardCvv: '',
}

export default function WaitlistModal() {
  const { isOpen, plan, planId, pagarmePublicKey, closeModal } = useModal()
  const [step, setStep] = useState(1)
  // Desfecho final do checkout: null (em fluxo) | { kind: 'success' }
  // | { kind: 'error', reason: 'card_declined' | 'pix_expired', message } | { kind: 'login_failed' }
  const [result, setResult] = useState(null)
  const [confirmClose, setConfirmClose] = useState(false)
  const [redirectIn, setRedirectIn] = useState(null)
  const [data, setData] = useState(EMPTY)
  const [erros, setErros] = useState({})
  const [showSenha, setShowSenha] = useState(false)
  const [showConfirmar, setShowConfirmar] = useState(false)
  const [loading, setLoading] = useState(false)
  const [erroGeral, setErroGeral] = useState(null)
  const [paymentMethod, setPaymentMethod] = useState('credit_card')
  const [pixQr, setPixQr] = useState(null)
  const [timeLeft, setTimeLeft] = useState(null)
  const [signatureEmpty, setSignatureEmpty] = useState(false)
  const [signatureData, setSignatureData] = useState(null)

  const canvasRef   = useRef(null)
  const drawingRef  = useRef(false)
  const hasDrawnRef = useRef(false)

  useEffect(() => {
    setStep(1)
    setResult(null)
    setConfirmClose(false)
    setRedirectIn(null)
    setData(EMPTY)
    setErros({})
    setShowSenha(false)
    setShowConfirmar(false)
    setErroGeral(null)
    setPaymentMethod('credit_card')
    setPixQr(null)
    setTimeLeft(null)
    setSignatureEmpty(false)
    setSignatureData(null)
  }, [planId])

  useEffect(() => {
    if (!pixQr?.expires_at) return

    const end = new Date(pixQr.expires_at).getTime()
    let finished = false

    const tick = () => {
      const remaining = Math.max(0, Math.floor((end - Date.now()) / 1000))
      setTimeLeft(remaining)
      if (remaining === 0 && !finished) {
        finished = true
        // não sobrescreve um sucesso que já tenha chegado pelo canal
        setResult(r => r || { kind: 'error', reason: 'pix_expired' })
      }
    }

    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [pixQr])

  // PIX: assina o canal da assinatura. Quando o webhook charge.paid ativa a
  // assinatura, o servidor empurra o status — sem polling.
  useEffect(() => {
    const subscriptionId = pixQr?.subscription_id
    if (!subscriptionId) return

    const consumer = createConsumer()
    const channel = consumer.subscriptions.create(
      { channel: 'Subscriptions::StatusChannel', id: subscriptionId },
      {
        received(data) {
          if (data.status === 'active') setResult({ kind: 'success' })
          else if (data.status === 'expired') setResult(r => r || { kind: 'error', reason: 'pix_expired' })
        },
      },
    )

    return () => {
      channel.unsubscribe()
      consumer.disconnect()
    }
  }, [pixQr])

  // Sucesso (cartão aprovado ou PIX confirmado): exibe o modal e redireciona
  // ao portal após uma breve contagem.
  useEffect(() => {
    if (result?.kind !== 'success') return

    let remaining = 4
    setRedirectIn(remaining)

    const interval = setInterval(() => {
      remaining -= 1
      setRedirectIn(remaining)
      if (remaining <= 0) {
        clearInterval(interval)
        loginAndEnter()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [result])

  async function loginAndEnter() {
    const loginRes = await fetch(`/auth/sign_in`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        customer: {
          email: data.email,
          password: data.senha,
        }
      }),
    })

    if (loginRes.ok) {
      window.location.href = '/portal'
    } else {
      // conta já criada, mas login automático falhou: orienta login manual
      setResult({ kind: 'login_failed' })
    }
  }

  // Fechar (X ou backdrop): se o PIX está pendente e ainda sem desfecho,
  // confirma antes de sair; caso contrário, fecha normalmente.
  function handleClose() {
    if (pixQr && !result && !confirmClose) {
      setConfirmClose(true)
      return
    }
    closeModal()
  }

  useEffect(() => {
    if (step !== 4 || !canvasRef.current) return

    hasDrawnRef.current = false

    const canvas = canvasRef.current
    const ratio  = window.devicePixelRatio || 1
    const rect   = canvas.getBoundingClientRect()
    canvas.width  = rect.width  * ratio
    canvas.height = rect.height * ratio

    const ctx = canvas.getContext('2d')
    ctx.scale(ratio, ratio)
    ctx.strokeStyle = '#1e293b'
    ctx.lineWidth   = 2
    ctx.lineCap     = 'round'
    ctx.lineJoin    = 'round'

    function getPos(e) {
      const r   = canvas.getBoundingClientRect()
      const src = e.touches ? e.touches[0] : e
      return { x: src.clientX - r.left, y: src.clientY - r.top }
    }

    function onStart(e) {
      e.preventDefault()
      drawingRef.current = true
      const p = getPos(e)
      ctx.beginPath()
      ctx.moveTo(p.x, p.y)
    }

    function onMove(e) {
      e.preventDefault()
      if (!drawingRef.current) return
      hasDrawnRef.current = true
      const p = getPos(e)
      ctx.lineTo(p.x, p.y)
      ctx.stroke()
    }

    function onEnd() { drawingRef.current = false }

    canvas.addEventListener('mousedown',  onStart)
    canvas.addEventListener('mousemove',  onMove)
    canvas.addEventListener('mouseup',    onEnd)
    canvas.addEventListener('mouseleave', onEnd)
    canvas.addEventListener('touchstart', onStart, { passive: false })
    canvas.addEventListener('touchmove',  onMove,  { passive: false })
    canvas.addEventListener('touchend',   onEnd)

    return () => {
      canvas.removeEventListener('mousedown',  onStart)
      canvas.removeEventListener('mousemove',  onMove)
      canvas.removeEventListener('mouseup',    onEnd)
      canvas.removeEventListener('mouseleave', onEnd)
      canvas.removeEventListener('touchstart', onStart)
      canvas.removeEventListener('touchmove',  onMove)
      canvas.removeEventListener('touchend',   onEnd)
    }
  }, [step])

  function set(field, value) {
    setData(d => ({ ...d, [field]: value }))
    if (erros[field]) setErros(e => ({ ...e, [field]: null }))
  }

  function validarEtapa1() {
    const e = {}
    if (!data.nome.trim()) e.nome = 'Nome obrigatório'
    if (!data.sobrenome.trim()) e.sobrenome = 'Sobrenome obrigatório'
    if (!data.email.trim()) e.email = 'E-mail obrigatório'
    else if (!isValidEmail(data.email)) e.email = 'E-mail inválido'
    if (!data.telefone.trim()) e.telefone = 'Telefone obrigatório'
    else if (data.telefone.replace(/\D/g, '').length < 10) e.telefone = 'Telefone inválido'
    setErros(e)
    return Object.keys(e).length === 0
  }

  function validarEtapa2() {
    const e = {}
    if (!data.cpfCnpj.trim()) e.cpfCnpj = 'CPF/CNPJ obrigatório'
    else if (!isValidCpfOrCnpj(data.cpfCnpj)) e.cpfCnpj = 'CPF/CNPJ inválido'
    if (!data.dataNascimento.trim()) e.dataNascimento = 'Data de nascimento obrigatória'
    else if (!isValidDate(data.dataNascimento)) e.dataNascimento = 'Data inválida'
    if (!data.senha) e.senha = 'Senha obrigatória'
    else if (data.senha.length < 8) e.senha = 'Mínimo 8 caracteres'
    if (!data.confirmarSenha) e.confirmarSenha = 'Confirmação obrigatória'
    else if (data.senha !== data.confirmarSenha) e.confirmarSenha = 'As senhas não coincidem'
    setErros(e)
    return Object.keys(e).length === 0
  }

  function validarEtapa3() {
    const e = {}
    if (data.cep.replace(/\D/g, '').length !== 8) e.cep = 'CEP inválido'
    if (!data.numero.trim()) e.numero = 'Número obrigatório'
    if (!data.cidade.trim()) e.cidade = 'Cidade obrigatória'
    if (!data.estado.trim()) e.estado = 'UF obrigatória'
    setErros(e)
    return Object.keys(e).length === 0
  }

  function validarEtapa4() {
    const e = {}
    if (data.cardNumber.replace(/\D/g, '').length < 13) e.cardNumber = 'Número do cartão inválido'
    if (!data.cardHolder.trim()) e.cardHolder = 'Nome do titular obrigatório'
    if (!/^\d{2}\/\d{2}$/.test(data.cardExpiry)) e.cardExpiry = 'Validade inválida'
    else if (Number(data.cardExpiry.slice(0, 2)) < 1 || Number(data.cardExpiry.slice(0, 2)) > 12) e.cardExpiry = 'Mês inválido'
    if (data.cardCvv.replace(/\D/g, '').length < 3) e.cardCvv = 'CVV inválido'
    setErros(e)
    return Object.keys(e).length === 0
  }

  async function buscarCep() {
    const cep = data.cep.replace(/\D/g, '')
    if (cep.length !== 8) return

    try {
      const res = await fetch(`/api/v1/addresses?cep=${cep}`, { headers: { Accept: 'application/json' } })
      const body = await res.json()

      if (!body.success) {
        setErros(e => ({ ...e, cep: 'CEP não encontrado' }))
        return
      }

      const a = body.data
      setData(d => ({
        ...d,
        rua: a.logradouro || d.rua,
        bairro: a.bairro || d.bairro,
        cidade: a.localidade || d.cidade,
        estado: a.uf || d.estado,
      }))
    } catch (_) {
      // silencioso: o usuário ainda pode preencher manualmente
    }
  }

  async function handleContinuar() {
    if (!validarEtapa1()) return

    try {
      await fetch(`/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: data.nome,
          sobrenome: data.sobrenome,
          email: data.email,
          telefone: data.telefone,
          plan_id: planId,
          plano: plan,
        }),
      })
    } catch (_) {
      // lead é best-effort, não bloqueia o fluxo
    }

    setStep(2)
  }

  function handleLimparCanvas() {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    hasDrawnRef.current = false
    setSignatureEmpty(false)
  }

  // Etapa 4 → 5: valida que há assinatura desenhada, recorta/centraliza e guarda
  // o PNG para enviar junto na criação do customer (na etapa de pagamento).
  async function handleConfirmarAssinatura() {
    if (!hasDrawnRef.current) {
      setSignatureEmpty(true)
      return
    }

    const dataUrl = canvasRef.current?.toDataURL('image/png')
    if (!dataUrl) {
      setSignatureEmpty(true)
      return
    }

    setSignatureEmpty(false)
    setSignatureData(await cropAndCenter(dataUrl))
    setStep(5)
  }

  async function handleSubmit() {
    const isCard = paymentMethod === 'credit_card'

    if (isCard && !validarEtapa4()) return

    if (isCard && !pagarmePublicKey) {
      setErroGeral('Pagamento indisponível no momento. Tente novamente mais tarde.')
      return
    }

    setLoading(true)
    setErroGeral(null)
    try {
      let payment = { method: 'pix' }

      if (isCard) {
        const cardToken = await tokenizeCard(pagarmePublicKey, {
          number: data.cardNumber,
          holder: data.cardHolder,
          expiry: data.cardExpiry,
          cvv: data.cardCvv,
        })

        if (!cardToken) {
          setErros(e => ({ ...e, cardNumber: 'Não foi possível validar o cartão. Confira os dados.' }))
          return
        }

        payment = { method: 'credit_card', token: cardToken }
      }

      const form = new FormData()
      form.append('plan[id]',                        planId)
      form.append('customer[first_name]',            data.nome)
      form.append('customer[last_name]',             data.sobrenome)
      form.append('customer[email]',                 data.email)
      form.append('customer[phone]',                 data.telefone)
      form.append('customer[document]',              data.cpfCnpj.replace(/\D/g, ''))
      form.append('customer[date_of_birth]',         data.dataNascimento.split('/').reverse().join('-'))
      form.append('customer[password]',              data.senha)
      form.append('customer[password_confirmation]', data.confirmarSenha)

      if (signatureData) {
        const blob = await (await fetch(signatureData)).blob()
        form.append('customer[signature]', blob, 'signature.png')
      }

      form.append('address[code]',         data.cep.replace(/\D/g, ''))
      form.append('address[street]',       data.rua)
      form.append('address[number]',       data.numero)
      form.append('address[complement]',   data.complemento)
      form.append('address[neighborhood]', data.bairro)
      form.append('address[city]',         data.cidade)
      form.append('address[state]',        data.estado)

      form.append('payment[method]', payment.method)
      if (payment.token) form.append('payment[token]', payment.token)

      const res = await fetch(`/api/v1/customers`, { method: 'POST', body: form })
      const body = await res.json().catch(() => ({}))

      if (!res.ok) {
        if (body?.errors?.email) {
          setErros(e => ({ ...e, email: 'E-mail já cadastrado' }))
          setStep(1)
        } else if (body?.errors?.cpf_cnpj) {
          setErros(e => ({ ...e, cpfCnpj: 'CPF/CNPJ já cadastrado' }))
          setStep(2)
        } else {
          setErroGeral('Erro ao realizar cadastro. Tente novamente.')
        }
        return
      }

      // PIX: mostra o QR Code para o cliente pagar (ativação vem depois do pagamento).
      if (!isCard) {
        setPixQr(body?.data?.pix || {})
        return
      }

      // Cartão: desfecho síncrono. O cadastro não falha quando o cartão é
      // recusado, então o sucesso/erro vem do status da cobrança no corpo.
      if (body?.data?.charge?.status === 'paid') {
        setResult({ kind: 'success' })
      } else {
        setResult({ kind: 'error', reason: 'card_declined', message: body?.data?.charge?.message })
      }
    } catch (_) {
      setErroGeral('Erro de conexão. Verifique sua internet.')
    } finally {
      setLoading(false)
    }
  }

  const TITLES = {
    1: 'Seus dados',
    2: 'Complete seu cadastro',
    3: 'Endereço',
    4: 'Sua assinatura',
    5: 'Pagamento',
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleClose}
        >
          <div className="absolute inset-0 bg-black/60" />

          <motion.div
            className="relative z-10 bg-surface-card rounded-card shadow-xl w-full max-w-lg p-8"
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 16 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-text-muted hover:text-text-primary transition-colors"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>

            {result?.kind === 'success' ? (
              <OutcomeScreen
                tone="success"
                icon={Check}
                title="Pagamento confirmado!"
                actions={
                  <button onClick={loginAndEnter} className="btn-primary w-full">
                    Ir para o portal agora
                  </button>
                }
              >
                Sua assinatura está ativa.{' '}
                {redirectIn !== null && (
                  <>Redirecionando em <span className="font-semibold text-text-primary">{redirectIn}s</span>…</>
                )}
              </OutcomeScreen>
            ) : result?.kind === 'error' ? (
              <OutcomeScreen
                tone="error"
                icon={AlertTriangle}
                title={result.reason === 'pix_expired' ? 'PIX expirado' : 'Pagamento não aprovado'}
                actions={
                  <button onClick={loginAndEnter} className="btn-primary w-full">
                    Ir para o portal
                  </button>
                }
              >
                {result.reason === 'pix_expired' ? (
                  'O tempo para pagar o PIX terminou. Sua conta já foi criada, no portal você pode gerar um novo pagamento.'
                ) : (
                  <>
                    {result.message || 'Não conseguimos aprovar seu cartão.'} Sua conta já foi criada, no portal você pode tentar novamente ou usar outro cartão.
                  </>
                )}
              </OutcomeScreen>
            ) : result?.kind === 'login_failed' ? (
              <OutcomeScreen
                tone="success"
                icon={Check}
                title="Cadastro concluído!"
                actions={
                  <a href="/portal" className="btn-primary w-full text-center">
                    Fazer login
                  </a>
                }
              >
                Sua conta foi criada. Faça login para acessar o portal.
              </OutcomeScreen>
            ) : confirmClose ? (
              <OutcomeScreen
                tone="info"
                icon={Clock}
                title="Seu PIX ainda está pendente"
                actions={
                  <>
                    <button onClick={() => setConfirmClose(false)} className="btn-primary w-full">
                      Continuar pagando
                    </button>
                    <button onClick={loginAndEnter} className="btn-ghost w-full">
                      Ir para o portal e pagar depois
                    </button>
                  </>
                }
              >
                Você ainda não concluiu o pagamento. Ele continua disponível na sua fatura em aberto no portal.
              </OutcomeScreen>
            ) : pixQr ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <h2 className="text-[20px] font-bold text-text-primary mb-2">Pague com PIX</h2>
                <p className="text-[13px] text-text-secondary max-w-xs mb-5">
                  Escaneie o QR Code ou copie o código abaixo. Após o pagamento você será redirecionado automaticamente.
                </p>

                {pixQr.qr_code_url && (
                  <img
                    src={pixQr.qr_code_url}
                    alt="QR Code PIX"
                    className="w-56 h-56 rounded-card border border-border-input mb-5"
                  />
                )}

                {pixQr.qr_code && (
                  <div className="w-full text-left">
                    <label className={LABEL}>Código copia e cola</label>
                    <div className="flex gap-2">
                      <input readOnly value={pixQr.qr_code} className={`${inputCls(false)} text-xs`} />
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(pixQr.qr_code)}
                        className="btn-primary flex-shrink-0"
                      >
                        Copiar
                      </button>
                    </div>
                  </div>
                )}

                <div className="mt-6 flex flex-col items-center gap-2">
                  {timeLeft !== null && (
                    <span className="text-[32px] font-mono font-bold text-text-primary tabular-nums">
                      {String(Math.floor(timeLeft / 60)).padStart(2, '0')}:{String(timeLeft % 60).padStart(2, '0')}
                    </span>
                  )}
                  <p className="flex items-center gap-2 text-[12px] text-text-muted">
                    <span className="w-2 h-2 rounded-full bg-accent-primary animate-pulse" />
                    Aguardando confirmação do pagamento…
                  </p>
                </div>
              </div>
            ) : (
              <>
                {erroGeral && (
                  <p className="text-[12px] text-status-error mb-4">{erroGeral}</p>
                )}

                <StepTabs current={step} />

                {plan && (
                  <p className="text-[13px] text-text-muted mb-1">
                    Você está reservando o{' '}
                    <span className="text-accent-primary font-semibold">Plano {plan}</span>
                  </p>
                )}

                <h2 className="text-[20px] font-bold text-text-primary mb-6">
                  {TITLES[step]}
                </h2>

            {/* ── Etapa 1 ── */}
            {step === 1 && (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Nome" error={erros.nome}>
                    <input
                      className={inputCls(erros.nome)}
                      placeholder="João"
                      value={data.nome}
                      onChange={e => set('nome', e.target.value)}
                    />
                  </Field>
                  <Field label="Sobrenome" error={erros.sobrenome}>
                    <input
                      className={inputCls(erros.sobrenome)}
                      placeholder="Silva"
                      value={data.sobrenome}
                      onChange={e => set('sobrenome', e.target.value)}
                    />
                  </Field>
                </div>

                <Field label="E-mail" error={erros.email}>
                  <input
                    type="email"
                    className={inputCls(erros.email)}
                    placeholder="joao@email.com"
                    value={data.email}
                    onChange={e => set('email', e.target.value)}
                  />
                </Field>

                <Field label="Telefone" error={erros.telefone}>
                  <input
                    type="tel"
                    className={inputCls(erros.telefone)}
                    placeholder="(81) 99999-9999"
                    value={data.telefone}
                    onChange={e => set('telefone', maskPhone(e.target.value))}
                  />
                </Field>

                <button onClick={handleContinuar} className="btn-primary w-full mt-2">
                  Continuar
                </button>
              </div>
            )}

            {/* ── Etapa 2 ── */}
            {step === 2 && (
              <div className="flex flex-col gap-4">
                <Field label="CPF / CNPJ" error={erros.cpfCnpj}>
                  <input
                    className={inputCls(erros.cpfCnpj)}
                    placeholder="000.000.000-00"
                    value={data.cpfCnpj}
                    onChange={e => set('cpfCnpj', maskCpfCnpj(e.target.value))}
                  />
                </Field>

                <Field label="Data de nascimento" error={erros.dataNascimento}>
                  <input
                    className={inputCls(erros.dataNascimento)}
                    placeholder="DD/MM/AAAA"
                    value={data.dataNascimento}
                    onChange={e => set('dataNascimento', maskDate(e.target.value))}
                  />
                </Field>

                <Field label="Senha" error={erros.senha}>
                  <div className="relative">
                    <input
                      type={showSenha ? 'text' : 'password'}
                      className={`${inputCls(erros.senha)} pr-11`}
                      placeholder="Mínimo 8 caracteres"
                      value={data.senha}
                      onChange={e => set('senha', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSenha(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                    >
                      {showSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </Field>

                <Field label="Confirmar senha" error={erros.confirmarSenha}>
                  <div className="relative">
                    <input
                      type={showConfirmar ? 'text' : 'password'}
                      className={`${inputCls(erros.confirmarSenha)} pr-11`}
                      placeholder="Repita a senha"
                      value={data.confirmarSenha}
                      onChange={e => set('confirmarSenha', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmar(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                    >
                      {showConfirmar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </Field>

                <div className="flex gap-3 mt-2">
                  <button onClick={() => { setStep(1); setErros({}) }} className="btn-ghost flex-shrink-0">
                    Voltar
                  </button>
                  <button onClick={() => { if (validarEtapa2()) setStep(3) }} className="btn-primary flex-1">
                    Continuar
                  </button>
                </div>
              </div>
            )}

            {/* ── Etapa 3 ── */}
            {step === 3 && (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-3 gap-4">
                  <Field label="CEP" error={erros.cep}>
                    <input
                      className={inputCls(erros.cep)}
                      placeholder="00000-000"
                      value={data.cep}
                      onChange={e => set('cep', maskCep(e.target.value))}
                      onBlur={buscarCep}
                    />
                  </Field>
                  <div className="col-span-2">
                    <Field label="Rua" error={erros.rua}>
                      <input
                        className={inputCls(erros.rua)}
                        placeholder="Av. General Justo"
                        value={data.rua}
                        onChange={e => set('rua', e.target.value)}
                      />
                    </Field>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <Field label="Número" error={erros.numero}>
                    <input
                      className={inputCls(erros.numero)}
                      placeholder="375"
                      value={data.numero}
                      onChange={e => set('numero', e.target.value)}
                    />
                  </Field>
                  <div className="col-span-2">
                    <Field label="Complemento" error={erros.complemento}>
                      <input
                        className={inputCls(erros.complemento)}
                        placeholder="Apto 201 (opcional)"
                        value={data.complemento}
                        onChange={e => set('complemento', e.target.value)}
                      />
                    </Field>
                  </div>
                </div>

                <Field label="Bairro" error={erros.bairro}>
                  <input
                    className={inputCls(erros.bairro)}
                    placeholder="Centro"
                    value={data.bairro}
                    onChange={e => set('bairro', e.target.value)}
                  />
                </Field>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Field label="Cidade" error={erros.cidade}>
                      <input
                        className={inputCls(erros.cidade)}
                        placeholder="Rio de Janeiro"
                        value={data.cidade}
                        onChange={e => set('cidade', e.target.value)}
                      />
                    </Field>
                  </div>
                  <Field label="UF" error={erros.estado}>
                    <input
                      className={inputCls(erros.estado)}
                      placeholder="RJ"
                      maxLength={2}
                      value={data.estado}
                      onChange={e => set('estado', e.target.value.toUpperCase())}
                    />
                  </Field>
                </div>

                <div className="flex gap-3 mt-2">
                  <button onClick={() => { setStep(2); setErros({}) }} className="btn-ghost flex-shrink-0">
                    Voltar
                  </button>
                  <button onClick={() => { if (validarEtapa3()) setStep(4) }} className="btn-primary flex-1">
                    Continuar
                  </button>
                </div>
              </div>
            )}

            {/* ── Etapa 4 ── */}
            {step === 4 && (
              <div className="flex flex-col gap-4">
                <p className="text-[13px] text-text-secondary -mt-2">
                  Desenhe sua assinatura no campo abaixo.
                </p>

                <canvas
                  ref={canvasRef}
                  style={{
                    width: '100%',
                    height: '160px',
                    border: `1px solid ${signatureEmpty ? 'var(--color-status-error, #ef4444)' : 'var(--color-border-input, #d1d5db)'}`,
                    borderRadius: '8px',
                    cursor: 'crosshair',
                    touchAction: 'none',
                    background: '#fff',
                    display: 'block',
                  }}
                />
                <button type="button" onClick={handleLimparCanvas}
                  className="btn-ghost self-start text-sm">
                  Limpar
                </button>

                {signatureEmpty && (
                  <p className="text-[11px] text-status-error -mt-2">
                    Desenhe sua assinatura para continuar
                  </p>
                )}

                <div className="flex gap-3 mt-2">
                  <button onClick={() => { setStep(3); setErros({}) }} className="btn-ghost flex-shrink-0">
                    Voltar
                  </button>
                  <button onClick={handleConfirmarAssinatura} className="btn-primary flex-1">
                    Continuar
                  </button>
                </div>
              </div>
            )}

            {/* ── Etapa 5 ── */}
            {step === 5 && (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => { setPaymentMethod('credit_card'); setErros({}) }}
                    className={methodCls(paymentMethod === 'credit_card')}
                  >
                    <span className="block text-sm font-semibold text-text-primary">Cartão de crédito</span>
                    <span className="block text-[12px] text-text-muted">Mensal recorrente</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPaymentMethod('pix'); setErros({}) }}
                    className={methodCls(paymentMethod === 'pix')}
                  >
                    <span className="block text-sm font-semibold text-text-primary">PIX</span>
                    <span className="block text-[12px] text-text-muted">Anual à vista</span>
                  </button>
                </div>

                {paymentMethod === 'credit_card' ? (
                  <>
                    <Field label="Número do cartão" error={erros.cardNumber}>
                      <input
                        inputMode="numeric"
                        autoComplete="cc-number"
                        className={inputCls(erros.cardNumber)}
                        placeholder="0000 0000 0000 0000"
                        value={data.cardNumber}
                        onChange={e => set('cardNumber', maskCardNumber(e.target.value))}
                      />
                    </Field>

                    <Field label="Nome do titular" error={erros.cardHolder}>
                      <input
                        autoComplete="cc-name"
                        className={inputCls(erros.cardHolder)}
                        placeholder="Como está impresso no cartão"
                        value={data.cardHolder}
                        onChange={e => set('cardHolder', e.target.value)}
                      />
                    </Field>

                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Validade" error={erros.cardExpiry}>
                        <input
                          inputMode="numeric"
                          autoComplete="cc-exp"
                          className={inputCls(erros.cardExpiry)}
                          placeholder="MM/AA"
                          value={data.cardExpiry}
                          onChange={e => set('cardExpiry', maskCardExpiry(e.target.value))}
                        />
                      </Field>
                      <Field label="CVV" error={erros.cardCvv}>
                        <input
                          inputMode="numeric"
                          autoComplete="cc-csc"
                          className={inputCls(erros.cardCvv)}
                          placeholder="000"
                          value={data.cardCvv}
                          onChange={e => set('cardCvv', e.target.value.replace(/\D/g, '').slice(0, 4))}
                        />
                      </Field>
                    </div>

                    <p className="text-[11px] text-text-muted">
                      Seus dados de cartão são enviados de forma segura à Pagar.me e não passam pelos nossos servidores.
                    </p>
                  </>
                ) : (
                  <div className="rounded-card border border-border-input bg-surface-page p-4 text-[13px] text-text-secondary">
                    Você pagará a anuidade <span className="font-semibold text-text-primary">à vista via PIX</span>. Ao confirmar, geramos um QR Code para você pagar e ativar a assinatura.
                  </div>
                )}

                <div className="flex gap-3 mt-2">
                  <button onClick={() => { setStep(4); setErros({}) }} className="btn-ghost flex-shrink-0">
                    Voltar
                  </button>
                  <button onClick={handleSubmit} disabled={loading} className="btn-primary flex-1">
                    {loading ? 'Processando...' : 'Garantir minha vaga'}
                  </button>
                </div>
              </div>
            )}
              </>
            )}

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
